import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@/shared/hooks/useUser';
import { Client } from '@/types/base';
import { GET } from '@/shared/api/requests';

/**
 * Hook to get the current client being viewed.
 * - If on a route with :clientName param, fetches that client's data
 * - Otherwise, returns the logged-in user's client
 * - For superusers viewing other clients' pages, shows the viewed client's logo
 */
export const useCurrentClient = () => {
  const { user } = useUser();
  const location = useLocation();
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);

  // Extract clientName from URL patterns like:
  // /app/dashboard/:clientName
  // /app/surveillance/:clientName/:projectName
  // /app/team/:clientName/:projectName
  const extractClientNameFromPath = (pathname: string): string | null => {
    // Match patterns: /app/dashboard/:clientName or /app/*/clientName/*
    const dashboardMatch = pathname.match(/\/app\/dashboard\/([^/]+)/);
    if (dashboardMatch) return dashboardMatch[1];

    const surveillanceMatch = pathname.match(/\/app\/surveillance\/([^/]+)/);
    if (surveillanceMatch) return surveillanceMatch[1];

    const teamMatch = pathname.match(/\/app\/team\/([^/]+)/);
    if (teamMatch) return teamMatch[1];

    // Match patterns like /app/property/:clientName/:projectId/...
    const propertyMatch = pathname.match(/\/app\/property\/([^/]+)\/\d+/);
    if (propertyMatch) return propertyMatch[1];

    return null;
  };

  useEffect(() => {
    const fetchClient = async () => {
      setLoading(true);
      
      const clientNameFromUrl = extractClientNameFromPath(location.pathname);
      
      // Determine which client name to fetch
      const clientNameToFetch = clientNameFromUrl || user?.client_info?.name;
      
      if (clientNameToFetch) {
        try {
          const response = await GET(`/base/dashboard/${clientNameToFetch}/`);
          const dashboardData = response.data;
          if (dashboardData?.client) {
            setCurrentClient(dashboardData.client);
          } else {
            setCurrentClient(null);
          }
        } catch (error) {
          console.error('Error fetching client from dashboard:', error);
          setCurrentClient(null);
        }
      } else {
        setCurrentClient(null);
      }
      
      setLoading(false);
    };

    if (user) {
      fetchClient();
    } else {
      setLoading(false);
    }
  }, [location.pathname, user]);

  return { currentClient, loading };
};

